import React, { useEffect, useState } from 'react';
import {
  ButtonTertiary,
  cssColorValue,
  IconChevronDown,
  IconChevronUp,
  screenSmMin,
  semanticColors,
  spacer12,
  spacer16,
  spacer24,
  spacer32,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';
import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';

import FeedbackWidgetContent from './components/FeedbackWidgetContent';
import useMobileView from './hooks/useMobileView';
import useIsHelpfulAnswer from './hooks/useIsHelpfulAnswer';
import useSubmitReason from './hooks/useSubmitReason';
import useTrackMessageUsLink from './hooks/useTrackMessageUsLink';

const StyledContainer = styled.div`
  border-top: 2px solid ${cssColorValue(semanticColors.decorativeSubdued)};
  padding: ${spacer8};
  padding-top: ${spacer32};
  margin-block-start: ${spacer8};

  @media (max-width: ${screenSmMin}) {
    padding-top: ${spacer16};
  }
`;

const StyledSticky = styled.div`
  padding: ${spacer24} 0;
  position: sticky;
  background-color: var(--background-base);
  bottom: 0;

  @media (max-width: ${screenSmMin}) {
    padding: ${spacer12};
  }
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${spacer8};
  align-content: flex-start;
  justify-content: space-between;
`;

interface FeedbackWidgetProps {
  articleIdentifier: string;
  ubi: UbiArticlePage;
}
const FeedbackWidget = ({ articleIdentifier, ubi }: FeedbackWidgetProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(true);

  const isMobileView = useMobileView();

  const { isHelpfulAnswered, answerIfIsHelpful } = useIsHelpfulAnswer({ ubi, articleIdentifier });
  const { isSubmittedReason, submitReason } = useSubmitReason({ ubi, articleIdentifier });
  const { trackMessageUsLink } = useTrackMessageUsLink({ ubi, articleIdentifier });

  useEffect(() => {
    ubi.logArticleHelpfulImpression();
  }, [ubi]);

  return (
    <StyledSticky>
      <StyledContainer>
        <StyledRow>
          <Type as="p" variant="balladBold" paddingBottom="1em">
            {t('feedback_widget_was_this_helpful')}
          </Type>
          {isMobileView && (
            <ButtonTertiary
              buttonSize={ButtonTertiary.sm}
              onClick={() => setOpen(!open)}
              cellPadding={0}
              data-testid="mobile-collapse-button"
            >
              {!open ? <IconChevronDown /> : <IconChevronUp />}
            </ButtonTertiary>
          )}
        </StyledRow>
        {(!isMobileView || open) && (
          <FeedbackWidgetContent
            isHelpfulAnswered={isHelpfulAnswered}
            answerIfIsHelpful={answerIfIsHelpful}
            isSubmittedReason={isSubmittedReason}
            submitReason={submitReason}
            trackMessageUsLink={trackMessageUsLink}
          />
        )}
      </StyledContainer>
    </StyledSticky>
  );
};

export default FeedbackWidget;
